/**
 * Phase 46 (Plan 46-07) — Notification route create/edit modal.
 *
 * Used by both:
 *   - GlobalNotificationRoutesPage (this plan, `scope="global"`)
 *   - ProjectNotificationsTab (Plan 46-08, `scope="project"`)
 *
 * Field rendering per UI-SPEC.md §Matrix add/edit modal:
 *   - Channel = email | in_app | slack_dm | sms → Target field is HIDDEN
 *     (target is forbidden per D-CHANNEL-02)
 *   - Channel = slack_channel | slack_thread → Target field
 *     is REQUIRED with per-channel hint copy
 *   - Save button disabled until eventKind + channel + (target when required)
 *     are set
 *   - Edit mode locks `eventKind` (matches backend update schema — only
 *     channel and target are mutable on PATCH).
 */
import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Btn } from '../ui/Btn';
import { Field } from '../ui/Field';
import { TextInput } from '../ui/TextInput';
import { DropdownSelect } from '../ui/DropdownSelect';
import {
  EVENT_KINDS,
  CHANNELS,
  EVENT_KIND_LABELS,
  CHANNEL_LABELS,
  CHANNEL_TOOLTIPS,
  type EventKind,
  type Channel,
} from '../../constants/notificationEnums';

export type EditorMode = 'create' | 'edit';
export type EditorScope = 'project' | 'global';

export interface EditorPayload {
  eventKind: EventKind;
  channel: Channel;
  target: string | null;
}

export interface NotificationRouteEditorModalProps {
  open: boolean;
  mode: EditorMode;
  scope: EditorScope;
  initialEventKind?: EventKind | null;
  initialChannel?: Channel | null;
  initialTarget?: string | null;
  conflictError?: string | null;
  isSaving?: boolean;
  /**
   * F3-004: prop type widened to accept async callers (`async (payload) =>
   * await mutation.mutateAsync(...)`). The modal awaits the returned promise
   * internally and surfaces rejections via `saveError` so a backend 4xx never
   * leaves the dialog stuck.
   */
  onSave: (payload: EditorPayload) => void | Promise<void>;
  onClose: () => void;
}

const TARGET_HINTS: Partial<Record<Channel, string>> = {
  slack_channel: 'Slack channel name (#channel) or channel ID (e.g. C0123ABC)',
  slack_thread: 'Thread permalink URL or channel_id:ts token',
};

export function NotificationRouteEditorModal({
  open,
  mode,
  scope,
  initialEventKind = null,
  initialChannel = null,
  initialTarget = null,
  conflictError = null,
  isSaving = false,
  onSave,
  onClose,
}: NotificationRouteEditorModalProps) {
  const [eventKind, setEventKind] = useState<EventKind | ''>(initialEventKind ?? '');
  const [channel, setChannel] = useState<Channel | ''>(initialChannel ?? '');
  const [target, setTarget] = useState<string>(initialTarget ?? '');
  const [targetError, setTargetError] = useState<string | null>(null);
  // F3-004: local error state for async onSave rejections.
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Sync initial values when modal re-opens with different (kind, channel).
  useEffect(() => {
    if (open) {
      setEventKind(initialEventKind ?? '');
      setChannel(initialChannel ?? '');
      setTarget(initialTarget ?? '');
      setTargetError(null);
      setSaveError(null);
      setPending(false);
    }
  }, [open, initialEventKind, initialChannel, initialTarget]);

  const targetMode = channel ? CHANNEL_LABELS[channel].target : 'none';
  const targetRequired = targetMode === 'required';

  const canSave =
    !!eventKind &&
    !!channel &&
    !isSaving &&
    !pending &&
    (!targetRequired || target.trim().length > 0);

  // F3-004: await async onSave callers and surface rejections inline; without
  // this the caller's `mutation.mutateAsync` rejection became an unhandled
  // promise and the modal stayed open with no signal.
  const handleSave = async () => {
    if (!eventKind || !channel) return;
    if (targetRequired) {
      if (target.trim().length === 0) {
        setTargetError('A target is required for this channel');
        return;
      }
    }
    setSaveError(null);
    setPending(true);
    try {
      await onSave({
        eventKind,
        channel,
        target: targetRequired ? target.trim() : null,
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setPending(false);
    }
  };

  const title =
    mode === 'create' ? 'Add notification route' : 'Edit notification route';
  const secondaryLabel =
    mode === 'create' ? 'Discard route' : 'Discard changes';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose} disabled={isSaving || pending}>
            {secondaryLabel}
          </Btn>
          <Btn
            variant="accent"
            onClick={handleSave}
            disabled={!canSave}
            isLoading={isSaving || pending}
          >
            Save route
          </Btn>
        </div>
      }
    >
      <div className="space-y-3" data-scope={scope}>
        {conflictError && (
          <div className="text-[12px] text-rose-600" role="alert">
            {conflictError}
          </div>
        )}
        {saveError && (
          <div className="text-[12px] text-rose-600" role="alert">
            {saveError}
          </div>
        )}
        <Field label="Event kind">
          <DropdownSelect
            value={eventKind}
            onChange={(e) => setEventKind(e.target.value as EventKind)}
            disabled={mode === 'edit'}
          >
            <option value="">— Select event kind —</option>
            {EVENT_KINDS.map((k) => (
              <option key={k} value={k}>
                {EVENT_KIND_LABELS[k]}
              </option>
            ))}
          </DropdownSelect>
        </Field>
        <Field label="Channel">
          <DropdownSelect
            value={channel}
            onChange={(e) => {
              const next = e.target.value as Channel | '';
              setChannel(next);
              setTargetError(null);
              if (next && CHANNEL_LABELS[next].target !== 'required') {
                setTarget('');
              }
            }}
          >
            <option value="">— Select channel —</option>
            {CHANNELS.map((c) => (
              <option key={c} value={c} title={CHANNEL_TOOLTIPS[c]}>
                {CHANNEL_LABELS[c].label}
              </option>
            ))}
          </DropdownSelect>
        </Field>
        {channel && targetRequired && (
          <Field
            label="Target"
            hint={TARGET_HINTS[channel]}
            error={targetError ?? undefined}
          >
            <TextInput
              value={target}
              onChange={(e) => {
                setTarget(e.target.value);
                setTargetError(null);
              }}
              placeholder={TARGET_HINTS[channel] ?? ''}
            />
          </Field>
        )}
      </div>
    </Modal>
  );
}
